import { isEmpty } from "lodash";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { SearchInput } from "../../../../components";
import { AssignmentPeopleContext, RolePermissions, SortingDirection } from "../../../../enums";
import { bindAction } from "../../../../interfaces";
import { type GenericFiltersMap } from "../../../../utils/filterUtils";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { DEFAULT_REQUEST_PARAMS } from "../../PeopleAssignments/Overview/helpers/constants";
import {
  resetSearch as resetSearchAction,
  setSearch as setSearchAction,
} from "../../PeopleAssignments/state/actions/userAssignmentActions";
import { resetSelectedItems, selectedItemsChanged } from "../state/slices/UsersSlice";
import { fetchUsersAction } from "../state/thunks/UsersThunk";

import UserInfoCell from "components/userInfoCell/UserInfoCell";
import { type ColumnOption } from "interfaces/columnOptions";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import { useContentAssignmentsPermissions } from "../../../../hooks/useContentAssignmentsPermissions";
import Restricted from "../../../Application/Restricted";
import ContentUsersNoResults from "../../Common/Fields/ContentUsersNoResults";
import { type ScormUser } from "../types/models";

enum UsersColumns {
  Name = "Name",
}

const ColumnToParamMap: {
  [P in Lowercase<UsersColumns>]: string;
} = {
  name: "firstName",
};

const getColumnOptions = (): ColumnOption<ScormUser>[] => {
  return [
    {
      name: UsersColumns.Name,
      width: 5,
      isSortable: true,
      disabled: (item) => item.inherited,
      render: (item) => <UserInfoCell deepLink={!item.inherited} user={item} />,
    },
  ];
};

const GET_SCORM_USERS_DEFAULT_PARAMS = {
  skip: 0,
  top: 10,
  orderBy: UsersColumns.Name,
  sortingDirection: SortingDirection.Descending,
};

interface UsersOverviewOwnProps {
  scormId: number;
  customHeaderContent: React.ReactElement;
}

export type UsersOverviewProps = UsersOverviewOwnProps & PropsFromRedux;

export const UsersOverview: FC<UsersOverviewProps> = ({
  scormId,
  customHeaderContent,
  fetchUsers,
  onUnmount,
  items,
  isLoading,
  itemsCount,
  onSelectedItemChanged,
}) => {
  const renderSearchInput = (): React.ReactElement => (
    <SearchInput placeholder="Search for Users..." onChange={onSearchChange} defaultValue={search} />
  );

  useEffect(
    () => () => onUnmount(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [search, setSearch] = useState<string>("");
  const reloadListItems = useRef<(enableSorting: boolean) => void>();

  const createReloadListItems = (reloadListItemsFunc: (enableSorting: boolean) => void) => {
    reloadListItems.current = reloadListItemsFunc;
  };

  const onSearchChange = async (searchTerm: string) => {
    setSearch(searchTerm);
    reloadListItems.current?.(isEmpty(searchTerm));
  };

  const getUsersHandler = useCallback(
    (
      skip: number = GET_SCORM_USERS_DEFAULT_PARAMS.skip,
      top: number = GET_SCORM_USERS_DEFAULT_PARAMS.top,
      orderBy: string = GET_SCORM_USERS_DEFAULT_PARAMS.orderBy,
      sortingDirection: SortingDirection = GET_SCORM_USERS_DEFAULT_PARAMS.sortingDirection,
      appliedFilter: GenericFiltersMap<string> = DEFAULT_REQUEST_PARAMS.appliedFilter,
    ) => {
      const sortingColumn = ColumnToParamMap[orderBy.toLowerCase() as Lowercase<UsersColumns>];
      fetchUsers({
        entityId: scormId,
        skip,
        top,
        sortingColumn,
        sortingDirection,
        filter: appliedFilter,
        term: search,
      });
    },
    [fetchUsers, scormId, search],
  );

  const permissionPredicate = useContentAssignmentsPermissions(
    AssignmentPeopleContext.User,
    RolePermissions.AssetsManage,
  );

  return (
    <Restricted
      permissions={[]}
      permissionPredicate={permissionPredicate}
      renderContent={() => (
        <>
          <GenericItemsView
            items={items}
            dataCount={itemsCount}
            isLoading={isLoading}
            onSelectedItemChanged={onSelectedItemChanged}
            defaultSortingColumnName={UsersColumns.Name}
            sortingDirection={SortingDirection.Ascending}
            columnOptions={getColumnOptions()}
            applyFilter={/* istanbul ignore next */ () => {}} // NOSONAR
            resetFilter={/* istanbul ignore next */ () => {}} // NOSONAR
            fetchData={getUsersHandler}
            renderSearch={renderSearchInput}
            customHeaderContent={customHeaderContent}
            isSelectDisabled={() => true}
            setReloadListItems={createReloadListItems}
            noResultsContent={<ContentUsersNoResults filtered={!!search} />}
          />
        </>
      )}
    />
  );
};
/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const edit = state.library.scorm.edit;
  return {
    items: edit.users.items,
    isLoading: edit.users.isLoading,
    itemsCount: edit.users.itemsCount,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchUsers: bindAction(fetchUsersAction, dispatch),
  setSearch: bindAction(setSearchAction, dispatch),
  onSelectedItemChanged: bindAction(selectedItemsChanged, dispatch),
  onUnmount: () => {
    dispatch(resetSearchAction());
    dispatch(resetSelectedItems());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UsersOverview);
export default ConnectedComponent;
